import React, { useState, useContext, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import * as googleNativo from "../platform/googleAuth";
import * as appleNativo from "../platform/appleAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEnvelope, FaLock, FaUser, FaStore, FaEye, FaEyeSlash, FaArrowLeft, FaCheck } from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import { theme } from "../theme";
import InAppBrowserWarning, { detectInAppBrowser } from "./InAppBrowserWarning";
import { isNative } from '../platform/native';
const logo = "/metria-logo.svg";

const TenantRegister = () => {
  const inAppBrowser = detectInAppBrowser();
  if (inAppBrowser) return <InAppBrowserWarning appName={inAppBrowser} />;

  return <TenantRegisterForm />;
};

const TenantRegisterForm = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: dados empresa, 2: escolher metodo
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Email flow
  const [metodo, setMetodo] = useState(null); // null | 'email' | 'google'
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [senhaConfirm, setSenhaConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Invite code
  // NUNCA dentro do app das lojas. O convite estende o periodo de teste, e a
  // Apple leu isso como "promo code para destravar desconto" fora da compra no
  // app — guideline 3.1.1, e foi motivo de rejeicao. Na web continua valendo:
  // ali nao ha loja para contrariar.
  const [inviteEnabled, setInviteEnabled] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteStatus, setInviteStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid'
  const [inviteInfo, setInviteInfo] = useState(null);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (isNative()) return;   // ver o comentario na declaracao de inviteEnabled
    api.get('/signup-config').then(r => setInviteEnabled(!!r.data.invite_code_enabled)).catch(() => {});
  }, []);

  // Auto-preenche código vindo por query string (?codigo=XXX ou ?invite=XXX)
  useEffect(() => {
    if (isNative()) return;   // nem por link: o app nao aceita convite
    const codigoUrl = searchParams.get('codigo') || searchParams.get('invite');
    if (codigoUrl) {
      const v = codigoUrl.toUpperCase();
      setInviteCode(v);
      validateInvite(v);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inviteDebounceRef = React.useRef(null);
  const inviteLatestRef = React.useRef('');

  const validateInvite = (code) => {
    const c = (code || '').trim().toUpperCase();
    inviteLatestRef.current = c;
    if (inviteDebounceRef.current) clearTimeout(inviteDebounceRef.current);
    if (!c) { setInviteStatus(null); setInviteInfo(null); return; }
    setInviteStatus('checking');
    inviteDebounceRef.current = setTimeout(async () => {
      try {
        const r = await api.post('/auth/validate-invite', { code: c });
        // Ignora resposta se o usuario ja digitou outra coisa
        if (inviteLatestRef.current !== c) return;
        setInviteStatus(r.data.valid ? 'valid' : 'invalid');
        setInviteInfo(r.data.valid ? r.data : null);
      } catch {
        if (inviteLatestRef.current !== c) return;
        setInviteStatus('invalid'); setInviteInfo(null);
      }
    }, 400);
  };

  const generateSlug = (v) => v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleNomeChange = (e) => {
    setNome(e.target.value);
  };

  // Gera slug unico automaticamente (com sufixo numerico se ja existir)
  const resolveUniqueSlug = async (baseName) => {
    let base = generateSlug(baseName) || 'confeitaria';
    if (base.length < 3) base = base + 'pro';
    let candidate = base;
    for (let i = 1; i < 50; i++) {
      try {
        const res = await api.get(`/tenants/check-slug/${candidate}`);
        if (res.data.available) return candidate;
      } catch { return candidate; }
      candidate = `${base}-${i + 1}`;
    }
    return `${base}-${Date.now()}`;
  };

  const handleNext = async () => {
    if (!nome.trim() || nome.trim().length < 2) {
      setError("Informe o nome da confeitaria."); return;
    }
    setError(null);
    setSlugStatus('checking');
    const unique = await resolveUniqueSlug(nome);
    setSlug(unique);
    setSlugStatus('available');
    setStep(2);
  };

  // O fluxo nativo devolve o mesmo idToken do web, entao cai no mesmo handler
  // e o backend nao muda.
  const criarComGoogleNativo = async () => {
    setError(null);
    const r = await googleNativo.entrar();
    if (r.ok) { await handleGoogleSuccess({ credential: r.idToken }); return; }
    if (r.motivo === 'cancelado') return;
    setError(r.motivo === 'sem-token'
      ? 'O Google não devolveu as credenciais. Crie com e-mail e senha.'
      : 'Não consegui abrir o login do Google agora.');
  };

  // Quem entrou pela Apple e ainda nao tem conta caia aqui e nao encontrava
  // opcao da Apple — ficava sem saida, tendo que criar conta por outro metodo
  // e depois nunca conseguir entrar pelo botao que usou da primeira vez.
  const criarComApple = async () => {
    setError(null);
    const r = await appleNativo.entrar();
    if (r.motivo === 'cancelado') return;
    if (!r.ok) {
      const detalhe = appleNativo.detalheDoErro && appleNativo.detalheDoErro();
      setError('Não consegui abrir o login da Apple agora.' + (detalhe ? ` [${String(detalhe).slice(0, 140)}]` : ''));
      return;
    }
    setLoading(true);
    try {
      // O nome so vem na PRIMEIRA autorizacao — vai junto para o backend gravar.
      const res = await api.post("/tenants/register", {
        nome, slug,
        identity_token: r.idToken,
        display_name: r.nome || undefined,
        invite_code: inviteCode || undefined,
      });
      const { access_token, refresh_token, is_admin, tenant } = res.data;
      login(access_token, is_admin, refresh_token, tenant);
    } catch (e) {
      setError(e?.response?.data?.erro || "Não consegui criar a conta com a Apple.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null); setLoading(true);
    try {
      const res = await api.post("/tenants/register", { nome, slug, credential: credentialResponse.credential, invite_code: inviteCode || undefined });
      const { access_token, refresh_token, is_admin, is_superadmin, tenant } = res.data;
      login(access_token, is_admin, refresh_token, tenant, is_superadmin);
    } catch (err) {
      setError(err.response?.data?.erro || "Erro ao criar conta.");
    } finally { setLoading(false); }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError(null);
    if (senha.length < 8) { setError("A senha deve ter pelo menos 8 caracteres."); return; }
    if (senha !== senhaConfirm) { setError("As senhas não conferem."); return; }
    setLoading(true);
    try {
      const res = await api.post("/tenants/register-email", {
        nome, slug, email, senha, display_name: displayName || email.split('@')[0],
        invite_code: inviteCode || undefined,
      });
      const { access_token, refresh_token, is_admin, tenant } = res.data;
      login(access_token, is_admin, refresh_token, tenant, false);
    } catch (err) {
      setError(err.response?.data?.erro || "Erro ao criar conta.");
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.page}>
      <div style={{ ...styles.blob, top: '-140px', right: '-80px', background: 'radial-gradient(circle, rgba(196,120,110,0.4), transparent 70%)' }} />
      <div style={{ ...styles.blob, bottom: '-140px', left: '-80px', background: 'radial-gradient(circle, rgba(232,180,160,0.5), transparent 70%)' }} />

      <div style={styles.card}>
        <div style={styles.logoWrapper}>
          <img src={logo} alt="Metria" style={styles.logo} />
        </div>
        <p style={styles.subtitle}>Crie sua confeitaria</p>

        {/* Trial chips */}
        <ul style={styles.trialChips} aria-label="Detalhes do teste">
          <li style={styles.trialChip}><span style={styles.trialCheck}>✓</span> 30 dias grátis</li>
          <li style={styles.trialChip}><span style={styles.trialCheck}>✓</span> R$&nbsp;39,90/mês depois</li>
          <li style={styles.trialChip}><span style={styles.trialCheck}>✓</span> Cancele quando quiser</li>
        </ul>

        {/* Stepper */}
        <div style={styles.stepper}>
          <div style={{ ...styles.stepCircle, background: theme.colors.primary, color: '#fff' }}>1</div>
          <div style={{ ...styles.stepLine, background: step >= 2 ? theme.colors.primary : theme.colors.border }} />
          <div style={{
            ...styles.stepCircle,
            background: step >= 2 ? theme.colors.primary : theme.colors.bgSection,
            color: step >= 2 ? '#fff' : theme.colors.textMuted,
          }}>2</div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {step === 1 && (
          <>
            <div style={styles.field}>
              <label style={styles.label}>Nome da confeitaria</label>
              <div style={styles.inputGroup}>
                <FaStore style={styles.inputIcon} />
                <input type="text" value={nome} onChange={handleNomeChange} placeholder="Ex: Doce Mel" style={styles.input} autoFocus />
              </div>
            </div>
            <button onClick={handleNext} disabled={slugStatus === 'checking'} style={{ ...styles.btnPrimary, opacity: slugStatus === 'checking' ? 0.6 : 1 }}>
              {slugStatus === 'checking' ? 'Verificando...' : 'Continuar'}
            </button>
          </>
        )}

        {step === 2 && !metodo && (
          <>
            <div style={styles.confirmBox}>
              <div style={{ fontSize: 13, color: theme.colors.textSecondary }}>Sua confeitaria</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, marginTop: 2 }}>{nome}</div>
            </div>

            {inviteEnabled && (
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>
                  Código de convite <span style={styles.hint}>(opcional)</span>
                </label>
                <input
                  value={inviteCode}
                  onChange={e => { const v = e.target.value.toUpperCase(); setInviteCode(v); validateInvite(v); }}
                  placeholder="METRIA-XXXXXX"
                  style={{ ...styles.input, paddingLeft: 14, borderColor: inviteStatus === 'valid' ? theme.colors.success : inviteStatus === 'invalid' ? theme.colors.danger : theme.colors.border }}
                />
                {inviteStatus === 'valid' && inviteInfo && (
                  <div style={{ fontSize: 12, color: theme.colors.success, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FaCheck size={10} /> {inviteInfo.trial_days} dias grátis aplicados
                  </div>
                )}
                {inviteStatus === 'invalid' && inviteCode && (
                  <div style={{ fontSize: 12, color: theme.colors.danger, marginTop: 6 }}>Código inválido ou expirado</div>
                )}
              </div>
            )}

            <p style={styles.chooseLabel}>Como você quer criar sua conta?</p>

            <button onClick={() => setMetodo('email')} style={styles.btnMethod}>
              <FaEnvelope style={{ color: theme.colors.primary }} />
              <span>Criar com email e senha</span>
            </button>

            <div style={styles.dividerText}>
              <span style={styles.dividerLine} /><span style={styles.dividerLabel}>ou</span><span style={styles.dividerLine} />
            </div>

            {loading ? (
              <p style={styles.info}>Criando sua conta...</p>
            ) : (
              <div style={styles.googleWrapper}>
                {/* No app o <GoogleLogin> nao renderiza — a origem e
                    https://localhost, que nao esta autorizada, e o Google bloqueia
                    OAuth em webview. Ficava um vazio depois do "ou", e quem entra
                    por Google (38% da base) nao conseguia nem criar conta. */}
                {googleNativo.disponivel() ? (
                  <button
                    type="button"
                    onClick={criarComGoogleNativo}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                      width: '100%', minHeight: 48, borderRadius: 10, cursor: 'pointer',
                      border: '1px solid #DADCE0', background: '#fff',
                      fontSize: 15, fontWeight: 500, color: '#3C4043',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
                    </svg>
                    Criar conta com Google
                  </button>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess} onError={() => setError("Login cancelado.")}
                    theme="outline" size="large" width="320" text="signup_with" shape="rectangular" locale="pt-BR"
                  />
                )}

                {/* So no iOS, com o mesmo tamanho do botao do Google. */}
                {appleNativo.disponivel() && (
                  <button
                    type="button"
                    onClick={criarComApple}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      width: '100%', minHeight: 48, borderRadius: 10, cursor: 'pointer',
                      border: 'none', background: '#000',
                      fontSize: 15, fontWeight: 500, color: '#fff',
                    }}
                  >
                    <svg width="17" height="20" viewBox="0 0 17 20" aria-hidden="true" fill="#fff">
                      <path d="M14.1 10.6c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2.1.8-1.2 1.2-2.4 1.2-2.4s-2.2-.9-2.2-3.3z"/>
                      <path d="M11.9 4.1c.6-.7 1-1.7.9-2.7-.9 0-2 .6-2.6 1.3-.6.6-1.1 1.7-.9 2.6 1 .1 2-.5 2.6-1.2z"/>
                    </svg>
                    Criar conta com Apple
                  </button>
                )}
              </div>
            )}

            <button onClick={() => { setStep(1); setError(null); }} style={styles.backLink}>
              <FaArrowLeft size={11} /> Voltar
            </button>
          </>
        )}

        {step === 2 && metodo === 'email' && (
          <form onSubmit={handleEmailRegister}>
            <div style={styles.field}>
              <label style={styles.label}>Seu nome</label>
              <div style={styles.inputGroup}>
                <FaUser style={styles.inputIcon} />
                <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Seu nome" style={styles.input} autoFocus />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <div style={styles.inputGroup}>
                <FaEnvelope style={styles.inputIcon} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="seu@email.com" style={styles.input} required />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Senha <span style={styles.hint}>(mínimo 8 caracteres)</span></label>
              <div style={styles.inputGroup}>
                <FaLock style={styles.inputIcon} />
                <input type={showPass ? "text" : "password"} value={senha} onChange={e => setSenha(e.target.value)} placeholder="Sua senha" style={{ ...styles.input, paddingRight: 40 }} required minLength={8} />
                <button type="button" onClick={() => setShowPass(!showPass)} style={styles.eyeBtn} tabIndex={-1}>
                  {showPass ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Confirme a senha</label>
              <div style={styles.inputGroup}>
                <FaLock style={styles.inputIcon} />
                <input type={showPass ? "text" : "password"} value={senhaConfirm} onChange={e => setSenhaConfirm(e.target.value)} placeholder="Repita a senha" style={styles.input} required />
              </div>
            </div>

            <button type="submit" disabled={loading} style={{ ...styles.btnPrimary, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Criando...' : 'Criar minha confeitaria'}
            </button>

            <button type="button" onClick={() => setMetodo(null)} style={styles.backLink}>
              <FaArrowLeft size={11} /> Outras opções
            </button>
          </form>
        )}

        <p style={styles.footerLink}>
          Já tem conta? <span style={styles.link} onClick={() => navigate("/login")}>Entrar</span>
        </p>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(155deg, #FDF7F4 0%, #F5E6E3 50%, #E8B4A0 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: theme.font, padding: '20px', position: 'relative', overflow: 'hidden',
  },
  blob: { position: 'absolute', width: 400, height: 400, borderRadius: '50%', pointerEvents: 'none', filter: 'blur(20px)' },
  card: {
    background: "rgba(255,255,255,0.98)", backdropFilter: "blur(20px)",
    borderRadius: 24, padding: "40px 36px", width: "100%", maxWidth: 440,
    textAlign: "center", boxShadow: "0 20px 60px rgba(61,44,44,0.15)",
    border: "1px solid rgba(255,255,255,0.6)", position: 'relative', zIndex: 1,
  },
  logoWrapper: { display: "flex", justifyContent: "center", marginBottom: 8 },
  logo: { width: 180, height: "auto", objectFit: "contain" },
  subtitle: {
    fontSize: 12, color: theme.colors.textSecondary, letterSpacing: 2.5,
    textTransform: "uppercase", margin: "0 0 16px 0", fontWeight: 500,
  },
  trialChips: {
    listStyle: 'none', padding: 0, margin: '0 0 24px',
    display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center',
  },
  trialChip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '5px 11px', borderRadius: 999,
    background: '#fff',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    fontSize: 12, fontWeight: 500,
    whiteSpace: 'nowrap',
    lineHeight: 1.3,
  },
  trialCheck: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 14, height: 14, borderRadius: '50%',
    background: theme.colors.success,
    color: '#fff', fontSize: 9, fontWeight: 900,
    flexShrink: 0,
  },
  stepper: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 24 },
  stepCircle: {
    width: 28, height: 28, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
    transition: 'all 0.3s',
  },
  stepLine: { width: 40, height: 2, transition: 'background 0.3s' },
  error: {
    color: theme.colors.danger, fontSize: 13, marginBottom: 16,
    background: theme.colors.dangerLight, borderRadius: 10,
    padding: '10px 14px', textAlign: 'left', border: `1px solid ${theme.colors.danger}30`,
  },
  field: { textAlign: "left", marginBottom: 14 },
  label: { fontSize: 13, fontWeight: 600, color: theme.colors.text, marginBottom: 6, display: "block" },
  hint: { fontSize: 11, fontWeight: 400, color: theme.colors.textMuted },
  inputGroup: { position: 'relative' },
  inputIcon: {
    position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
    color: theme.colors.textMuted, fontSize: 14, pointerEvents: 'none',
  },
  prefix: {
    position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
    color: theme.colors.textMuted, fontSize: 13, pointerEvents: 'none',
  },
  input: {
    width: "100%", padding: "12px 14px 12px 42px", fontSize: 14,
    border: `1.5px solid ${theme.colors.border}`, borderRadius: 12,
    background: theme.colors.bgSection, color: theme.colors.text,
    outline: 'none', boxSizing: 'border-box', fontFamily: theme.font,
  },
  eyeBtn: {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: theme.colors.textMuted,
    cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center',
  },
  slugHint: {
    fontSize: 12, marginTop: 6, display: "flex", alignItems: 'center', gap: 4,
    color: theme.colors.textMuted,
  },
  btnPrimary: {
    width: '100%', padding: '14px', fontSize: 15, fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
    color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer',
    marginTop: 6, boxShadow: '0 4px 14px rgba(196,120,110,0.3)', fontFamily: theme.font,
  },
  btnMethod: {
    width: '100%', padding: '13px 16px', fontSize: 14, fontWeight: 600,
    background: theme.colors.bgSection, color: theme.colors.text,
    border: `1.5px solid ${theme.colors.border}`, borderRadius: 12,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, fontFamily: theme.font, transition: 'all 0.2s',
  },
  confirmBox: {
    background: theme.colors.primaryLight, borderRadius: 12, padding: '14px 16px',
    marginBottom: 20, textAlign: 'center',
  },
  chooseLabel: {
    fontSize: 13, fontWeight: 600, color: theme.colors.text,
    marginBottom: 12, textAlign: 'center',
  },
  dividerText: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: '16px 0', color: theme.colors.textMuted, fontSize: 12,
  },
  dividerLine: { flex: 1, height: 1, background: theme.colors.border },
  dividerLabel: { whiteSpace: 'nowrap' },
  googleWrapper: {
    display: "flex", flexDirection: "column", gap: 10,
    alignItems: "stretch", marginBottom: 8,
  },
  info: { fontSize: 14, color: theme.colors.textSecondary, margin: 12 },
  backLink: {
    background: "none", border: "none", color: theme.colors.textSecondary,
    fontSize: 12, cursor: "pointer", marginTop: 14, display: 'inline-flex',
    alignItems: 'center', gap: 4, padding: 6,
  },
  footerLink: {
    fontSize: 13, color: theme.colors.textMuted, margin: '20px 0 0',
    paddingTop: 16, borderTop: `1px solid ${theme.colors.border}`,
  },
  link: { color: theme.colors.primary, cursor: "pointer", fontWeight: 600 },
};

export default TenantRegister;
